import { useEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import Button from './Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  /** Dismiss button text — set it when the action itself is a "cancel" (e.g. "Keep it"). */
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    if (!open) return;
    // Save + restore the prior value rather than hardcoding ''. When this
    // confirm is shown over an already-open Modal (e.g. the wizard's
    // discard-changes prompt), the Modal left body overflow at 'hidden';
    // clearing it on cancel would unlock background scroll behind the still-open
    // wizard. Restoring the captured value keeps the lock intact.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => cancelRef.current?.focus(), 0);
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Stable onCancel ref so the listener-binding effect doesn't re-fire on
  // every parent re-render (parents typically pass inline arrows).
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

  useEffect(() => {
    if (open) {
      const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelRef.current(); };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;

  return createPortal(
    <div className="z-modal__overlay">
      <div className="z-modal__backdrop" onClick={onCancel} />
      <div
        ref={dialogRef}
        className={cn('z-confirm-dialog')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        onKeyDown={handleKeyDown}
      >
        <div className="z-confirm-dialog__header">
          <div className="z-confirm-dialog__icon-wrap">
            <FontAwesomeIcon icon={faTriangleExclamation} className="z-confirm-dialog__icon" />
          </div>
          <div>
            <h3 id={titleId} className="z-confirm-dialog__title">{title}</h3>
            <p id={messageId} className="z-confirm-dialog__message">{message}</p>
          </div>
        </div>
        <div className="z-confirm-dialog__actions">
          <Button ref={cancelRef} variant="secondary" size="sm" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={variant} size="sm" onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
